import type { TaxIncentiveJurisdiction } from "types/tax-incentive-catalog";
import {
  StructuredTaxIncentiveProgram,
  TaxIncentiveFrictionDefaults,
  TaxIncentiveLoanOutRules,
  TaxIncentivePayoutRules,
  TaxIncentiveQualifiedSpendRules,
  TaxIncentiveUplift,
} from "./taxIncentiveCatalog.types";
import { TaxIncentiveCatalogDetail, TaxIncentiveCatalogRow } from "./taxIncentivesCatalog.utils";

function asUplifts(value: unknown): TaxIncentiveUplift[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is TaxIncentiveUplift =>
      typeof item === "object" &&
      item != null &&
      typeof (item as TaxIncentiveUplift).label === "string" &&
      typeof (item as TaxIncentiveUplift).rate === "number"
  );
}

function asQualifiedSpend(value: unknown): TaxIncentiveQualifiedSpendRules {
  if (!value || typeof value !== "object") {
    return { atlEligible: false, btlEligible: false };
  }
  const record = value as TaxIncentiveQualifiedSpendRules;
  return {
    atlEligible: Boolean(record.atlEligible),
    btlEligible: Boolean(record.btlEligible),
    residentLaborUpliftRate: record.residentLaborUpliftRate,
    nonResidentLaborPenaltyRate: record.nonResidentLaborPenaltyRate,
  };
}

function asLoanOut(value: unknown): TaxIncentiveLoanOutRules {
  if (!value || typeof value !== "object") {
    return { required: false };
  }
  return value as TaxIncentiveLoanOutRules;
}

function asPayout(
  value: unknown,
  incentiveType: StructuredTaxIncentiveProgram["incentiveType"]
): TaxIncentivePayoutRules {
  if (value && typeof value === "object" && "estimatedMonths" in value) {
    return value as TaxIncentivePayoutRules;
  }
  return { type: incentiveType, estimatedMonths: 18 };
}

function asFriction(value: unknown): TaxIncentiveFrictionDefaults {
  if (!value || typeof value !== "object") return {};
  return value as TaxIncentiveFrictionDefaults;
}

export function mapCatalogStatusToRowStatus(status: TaxIncentiveJurisdiction["status"]): string {
  if (status === "active") return "Active";
  if (status === "inactive") return "Inactive";
  return "Unknown";
}

export function mapJurisdictionToCatalogDetail(
  jurisdiction: TaxIncentiveJurisdiction
): TaxIncentiveCatalogDetail {
  return {
    typeOfIncentive: jurisdiction.type_of_incentive,
    applicationWebsite: jurisdiction.application_website,
    incentiveDetailsWebsite: jurisdiction.incentive_details_website,
    minimumSpend: jurisdiction.minimum_spend_text,
    annualCap: jurisdiction.annual_cap_text,
    projectCap: jurisdiction.project_cap_text,
    terms: jurisdiction.terms,
    additionalNotes: jurisdiction.additional_notes,
  };
}

function parseMinimumSpendTerms(terms?: string): string[] {
  if (!terms) return ["See program terms"];
  const segments = terms
    .split(/(?<=[.;])\s+/)
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.length > 0 ? segments : ["See program terms"];
}

export function mapJurisdictionToCatalogRow(
  jurisdiction: TaxIncentiveJurisdiction
): TaxIncentiveCatalogRow {
  const detail = mapJurisdictionToCatalogDetail(jurisdiction);
  const typeLabel = detail.typeOfIncentive ?? "—";

  return {
    jurisdictionCode: jurisdiction.jurisdiction_code,
    lastReviewedAt: jurisdiction.last_reviewed_at,
    location: {
      name: jurisdiction.jurisdiction_name,
      country: jurisdiction.country,
    },
    ranking: detail.minimumSpend ?? "—",
    status: mapCatalogStatusToRowStatus(jurisdiction.status),
    typeLabel,
    detail,
    actions: {
      requirements: parseMinimumSpendTerms(detail.terms ?? detail.minimumSpend),
      details:
        detail.additionalNotes ??
        detail.terms ??
        detail.typeOfIncentive ??
        "No additional details available.",
    },
  };
}

export function mapJurisdictionToStructuredProgram(
  jurisdiction: TaxIncentiveJurisdiction
): StructuredTaxIncentiveProgram {
  const isActive = jurisdiction.status === "active" && jurisdiction.incentive_type !== "none";

  return {
    jurisdictionName: jurisdiction.jurisdiction_name,
    incentiveType: jurisdiction.incentive_type,
    baseRate: jurisdiction.base_rate ?? 0,
    maxHeadlineRate: jurisdiction.max_headline_rate ?? 0,
    uplifts: asUplifts(jurisdiction.uplifts),
    minimumSpend: jurisdiction.minimum_spend ?? undefined,
    caps: {
      annual: jurisdiction.annual_cap ?? undefined,
      project: jurisdiction.project_cap ?? undefined,
    },
    qualifiedSpend: asQualifiedSpend(jurisdiction.qualified_spend),
    loanOut: asLoanOut(jurisdiction.loan_out),
    payout: asPayout(jurisdiction.payout, jurisdiction.incentive_type),
    friction: asFriction(jurisdiction.friction),
    sunsetDate: jurisdiction.sunset_date ?? undefined,
    isActive,
  };
}

export function buildTaxIncentiveCatalogRowsFromJurisdictions(
  jurisdictions: TaxIncentiveJurisdiction[]
): TaxIncentiveCatalogRow[] {
  return jurisdictions.map(mapJurisdictionToCatalogRow);
}

export function findJurisdictionByName(
  jurisdictions: TaxIncentiveJurisdiction[],
  name: string
): TaxIncentiveJurisdiction | undefined {
  return jurisdictions.find((jurisdiction) => jurisdiction.jurisdiction_name === name);
}
